import type {
  DayPart,
  PlanningEntityIdentifier,
  PlanningEntityType,
  TransportationMode
} from 'generated';
import styled from 'styled-components';

import type { Event } from '../../types';

import { CARD_WIDTH } from 'constants/widths';

import { Box } from '@mui/material';

import { ActivityCardPragmatic } from '../ActivityCardPragmatic/ActivityCardPragmatic';
import DropzonePragmatic from './DropzonePragmatic';
import FreeTimePragmatic from '../FreeTimePragmatic/FreeTimePragmatic';
import TravelTimePragmatic from '../TravelTimePragmatic/TravelTimePragmatic';
import DraggableCardPragmatic from '../DraggableCardPragmatic/DraggableCardPragmatic';
import { useEffect, useState } from 'react';

const List = styled(Box)<{ height?: string }>`
  display: flex;
  flex-direction: column;
  width: ${CARD_WIDTH}px;
  height: ${({ height }) => height || '100%'};
  padding: 10px;
  list-style: none;

  .freetime-container:last-child {
    flex-grow: 1;
    padding: 0;
  }

  .dropzone:last-child {
    flex-grow: 1;

    .dropzone__indicator {
      top: 0px;
    }
  }
` as typeof Box;

const Container = styled(Box)`
  margin-top: 8px;
`;

interface InnerListPragmaticProps {
  activeEntityContainingSelection: string | null;
  dayPart?: DayPart;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  planningEntityIdentifier?: PlanningEntityIdentifier;
  scheduleStart: string;
  scheduleId?: string;
  readOnly?: boolean;
  events: Event[];
  transportationMode?: TransportationMode;
  onChangeActiveEntity?: (entityId: string) => void;
  onChangeSelection?: (selectionIds: string[]) => void;
}

const InnerListPragmatic = ({
  activeEntityContainingSelection,
  dayPart = 'MORNING',
  events,
  planningEntityType,
  planningEntityId,
  planningEntityIdentifier,
  scheduleStart,
  scheduleId,
  transportationMode,
  readOnly,
  onChangeActiveEntity,
  onChangeSelection
}: InnerListPragmaticProps) => {
  'use memo';
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const isActiveEntity: boolean =
    activeEntityContainingSelection === (scheduleId || planningEntityId);

  const onHandleSelect = (id: string) => {
    setSelectedIds((selectedIds) => {
      onChangeActiveEntity?.(scheduleId || planningEntityId);

      if (selectedIds.includes(id))
        return selectedIds.filter((value) => value !== id);
      if (!selectedIds.length) return [id];
      return selectedIds.concat(id);
    });
  };

  useEffect(() => {
    if (!isActiveEntity)
      setSelectedIds((prev) => (prev.length > 0 ? [] : prev));
  }, [isActiveEntity]);

  useEffect(() => {
    onChangeSelection?.(selectedIds);
  }, [onChangeSelection, selectedIds]);

  return (
    <List component="ul">
      {events.map((a, i: number) => {
        const key = `${
          a?.start || a?.scheduleStart || a?.category
        }-${a?.id}-${i}`;

        if (a?.category === 'TRAVELTIME') {
          return (
            <TravelTimePragmatic
              key={key}
              id={a.id}
              dayPart={dayPart}
              planningEntityType={planningEntityType}
              planningEntityId={planningEntityId}
              planningEntityIdentifier={planningEntityIdentifier!}
              droppable={a.droppable}
              durationInSeconds={a.durationInSeconds}
              scheduleStart={scheduleStart}
              start={a.start}
              transportationMode={transportationMode}
            />
          );
        }

        if (a?.category === 'FREE') {
          return (
            <FreeTimePragmatic
              key={key}
              id={a.id}
              dayPart={dayPart}
              planningEntityType={planningEntityType}
              planningEntityId={planningEntityId}
              planningEntityIdentifier={planningEntityIdentifier!}
              durationInSeconds={a.durationInSeconds}
              scheduleStart={scheduleStart}
              start={a.start}
            />
          );
        }

        if (a?.category === 'DROPZONE') {
          return (
            <DropzonePragmatic
              key={key}
              id={a.id}
              planningEntityType={planningEntityType}
              planningEntityId={planningEntityId}
              planningEntityIdentifier={planningEntityIdentifier!}
              scheduleStart={scheduleStart}
              start={a.start}
            />
          );
        }

        if (planningEntityId === '') {
          return (
            <Container key={key}>
              <ActivityCardPragmatic
                id={a.id}
                appointmentGrouping={a.appointmentGrouping}
                selected={false}
                planningEntityId={planningEntityId}
                planningEntityType={planningEntityType}
                organizationalUnit={a.organizationalUnit}
                activity={a.activity}
                workPlace={a.workPlace}
                project={a.project}
                employeeType={a.employeeType}
                clients={a.clients}
                employees={a.employees}
                noteInternal={a.noteInternal}
                durationInSeconds={a.durationInSeconds}
                warnings={a.warnings}
                isAssigned={a.isAssigned}
                isAbsent={a.isAbsent}
                absenceReason={a.absenceReason}
                isEditable={a.isEditable}
                isCreditable={a.isCreditable}
                isCredit={a.isCredit}
                isRecurring={a.isRecurring}
                isRecurringException={a.isRecurringException}
                isOpen={a.isOpen}
                recurrenceRule={a.recurrenceRule}
                start={a.start}
                timeWindow={a.timeWindow}
                location={a.location}
                schedulingLabel={a.schedulingLabel}
                createdOn={a.createdOn}
                createdByName={a.createdByName}
                lastUpdatedOn={a.lastUpdatedOn}
                lastUpdatedByName={a.lastUpdatedByName}
              />
            </Container>
          );
        }

        return (
          <DraggableCardPragmatic
            key={key}
            id={a.id}
            appointmentGrouping={a.appointmentGrouping}
            readOnly={readOnly}
            planningEntityType={planningEntityType}
            planningEntityId={planningEntityId}
            onHandleSelect={onHandleSelect}
            scheduleStart={scheduleStart}
            scheduleId={scheduleId}
            organizationalUnit={a.organizationalUnit}
            activity={a.activity}
            workPlace={a.workPlace}
            project={a.project}
            employeeType={a.employeeType}
            clients={a.clients}
            employees={a.employees}
            noteInternal={a.noteInternal}
            durationInSeconds={a.durationInSeconds}
            warnings={a.warnings}
            isAbsent={a.isAbsent}
            absenceReason={a.absenceReason}
            isAssigned={a.isAssigned}
            isEditable={a.isEditable}
            isCreditable={a.isCreditable}
            isCredit={a.isCredit}
            isRecurring={a.isRecurring}
            isRecurringException={a.isRecurringException}
            isOpen={a.isOpen}
            recurrenceRule={a.recurrenceRule}
            start={a.start}
            timeWindow={a.timeWindow}
            location={a.location}
            schedulingLabel={a.schedulingLabel}
            selected={selectedIds.includes(a.id)}
            selectionCount={selectedIds.length}
            createdOn={a.createdOn}
            createdByName={a.createdByName}
            lastUpdatedOn={a.lastUpdatedOn}
            lastUpdatedByName={a.lastUpdatedByName}
          />
        );
      })}
    </List>
  );
};

export default InnerListPragmatic;
